import React, { useState, useRef, useEffect } from "react";
import { PlaySound } from "~/SoundUtils";
import { useAtom } from "jotai";
import { volumeAtom } from "~/Atom";
import "~/css/Accordion.css"; // 基本レイアウト
import "~/css/NestedAccordion.css"; // 入れ子専用スタイル

// 入れ子用: アニメーションの時間定数
const transitionDuration = "0.35s";
const transitionTiming = "ease";

interface NestedAccordionItemProps {
  title: string | React.JSX.Element;
  children: React.ReactNode;
  dynamicHeight?: boolean;
  summaryClassName?: string;
  defaultOpen?: boolean;
}

/**
 * 入れ子専用アコーディオンアイテム
 */
const NestedAccordionItem: React.FC<NestedAccordionItemProps> = ({
  title,
  children,
  dynamicHeight = true,
  summaryClassName = "",
  defaultOpen = false,
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [height, setHeight] = useState<string>(defaultOpen ? "auto" : "0px");
  const contentRef = useRef<HTMLDivElement>(null);
  const [volume] = useAtom(volumeAtom);

  const toggle = () => {
    setIsOpen((o) => !o);
    PlaySound("push", volume);
  };

  useEffect(() => {
    if (!contentRef.current) return;
    if (isOpen) {
      const sh = contentRef.current.scrollHeight;
      setHeight(`${sh}px`);
      setTimeout(() => {
        // 時間差で高さをautoに調整
        if (dynamicHeight) setHeight("auto");
      }, 350);
    } else if (contentRef.current) {
      const sh = contentRef.current.scrollHeight;
      requestAnimationFrame(() => {
        setHeight(`${sh}px`);
        requestAnimationFrame(() => setHeight("0px"));
      });
    }
  }, [isOpen, children, dynamicHeight]);

  return (
    <div className={`nested-accordion-item ${isOpen ? "open" : ""}`}>
      <button
        className={`accordion-summary nested-summary ${
          isOpen ? "is-active" : ""
        } ${summaryClassName}`.trim()}
        onClick={toggle}
        aria-expanded={isOpen}
      >
        {title}
        <span className="material-symbols-rounded accordion-icon-child">stat_minus_1</span>
      </button>
      <div
        ref={contentRef}
        className="accordion-content nested-content"
        style={{
          height: height,
          transition: `height ${transitionDuration} ${transitionTiming}`,
        }}
      >
        <div className="content-wrapper nested-content-wrapper">{children}</div>
      </div>
    </div>
  );
};

export default NestedAccordionItem;
